import React from 'react';

import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  Space,
  Text,
  TextInput,
  Title,
  Tooltip,
  createStyles,
  useMantineTheme,
} from '@mantine/core';
import { IconChevronLeft, IconFilterOff, IconSearch, IconX } from '@tabler/icons-react';
import AthleteStatusSelect from 'components/form-fields/select-fields/AthleteStatusSelect';
import { useNavigate, useParams } from 'react-router-dom';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.lg,
    background: theme.colors.gray[0],
    borderRadius: 0,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    display: 'grid',
    gridTemplateColumns: 'max-content auto auto auto min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `
        'title title title title button button'
        'lastName search1 search2 search3 search3 clear'
      `,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'max-content auto auto auto min-content min-content min-content',
      gridTemplateAreas: `
        'title title title title button button'
        'search1 search1 search2 search3 search3 search3'
        'lastName  . . . . clear'
      `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'auto min-content min-content',
      gridTemplateAreas: `
        'title button button'
        'search1 search1 search1'
        'search2 search2 search2'
        'search3 search3 search3'
        'lastName . clear'
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  lastName: {
    gridArea: 'lastName',
  },
  search1: {
    gridArea: 'search1',
    minWidth: '18rem',
  },
  search2: {
    gridArea: 'search2',
  },
  search3: {
    gridArea: 'search3',
  },
  clear: {
    gridArea: 'clear',
  },
  button: {
    gridArea: 'button',
  },
}));

interface FilterTerms {
  searchTerm: string;
  isArrangeByLastName: boolean;
  athleteStatus: string;
  loginEnabled: string;
}

const AdminAthletesPageHeader = ({
  searchFilters,
  initialSearchFiltersValue,
  setSearchFilters,
}: {
  searchFilters: FilterTerms;
  initialSearchFiltersValue: FilterTerms;
  setSearchFilters: React.Dispatch<React.SetStateAction<FilterTerms>>;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { gymId } = useParams();
  const navigate = useNavigate();
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.currentTarget?.value || '';
    setSearchFilters(prevValue => ({ ...prevValue, searchTerm: value }));
  };
  const handleBackClick = (): void => {
    navigate(-1);
  };
  const handleIsArrangeByLastNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchFilters(prevValue => ({ ...prevValue, isArrangeByLastName: !!e.currentTarget?.checked }));
  };
  const handleAthleteStatusChange = (value: string | null): void => {
    setSearchFilters(prevValue => ({ ...prevValue, athleteStatus: value || '' }));
  };
  const handleAthleteLoginStatusChange = (value: string | null): void => {
    setSearchFilters(prevValue => ({ ...prevValue, loginEnabled: value || '' }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
  };
  const handleClearSearchFilter = (): void => {
    setSearchFilters(prevValue => ({ ...prevValue, searchTerm: '' }));
  };

  const handleViewStaff = (): void => {
    window.location.href = `/gyms/${gymId}/staff`;
  };

  return (
    <Paper className={classes.header}>
      <Group>
        <ActionIcon onClick={handleBackClick}>
          <IconChevronLeft size={theme.fontSizes.xl} />
        </ActionIcon>
        <Title order={1} size='h1' pr='xl' className={classes.title}>
          Athletes
        </Title>
      </Group>
      <Checkbox
        className={classes.lastName}
        checked={searchFilters.isArrangeByLastName}
        onChange={handleIsArrangeByLastNameChange}
        label={
          <Text color='gray' fs='italic'>
            Arrange by Last Name
          </Text>
        }
      />

      <TextInput
        className={classes.search1}
        icon={<IconSearch size={theme.fontSizes.md} />}
        placeholder='Search by name, email, phone'
        value={searchFilters?.searchTerm}
        onChange={handleSearchChange}
        rightSection={
          !!searchFilters.searchTerm ? (
            <ActionIcon onClick={handleClearSearchFilter} size='md'>
              <IconX size={theme.fontSizes.md} />
            </ActionIcon>
          ) : (
            <Space />
          )
        }
      />

      <AthleteStatusSelect
        className={classes.search2}
        placeholder='Select status'
        value={searchFilters?.athleteStatus}
        onChange={handleAthleteStatusChange}
        clearable
      />

      <Select
        className={classes.search3}
        placeholder='Select login status'
        value={searchFilters?.loginEnabled}
        onChange={handleAthleteLoginStatusChange}
        data={[
          { label: 'Enabled', value: 'true' },
          { label: 'Disabled', value: 'false' },
        ]}
        clearable
      />

      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>

      <Button className={classes.button} onClick={handleViewStaff}>
        View Staff
      </Button>
    </Paper>
  );
};

export default AdminAthletesPageHeader;
